import { memo } from "react";
import {
  Box,
  Stack,
  Card,
  CardContent,
  Avatar,
  Typography,
} from "@mui/material";
import CommentIcon from "@mui/icons-material/Comment";
import AttachmentIcon from "@mui/icons-material/Attachment";
import ImageIcon from "@mui/icons-material/Image";
import ImageCarousel from "./ImageCarousel";
import ModernCard from "./ModernCard";
import PropTypes from "prop-types";

const CommentsTable = memo(({ comments, formatCreatedAt, onFileClick, t }) => (
  <ModernCard
    title={t("documentDetail.commentsTitle")}
    icon={CommentIcon}
    color="info"
  >
    {comments.length > 0 ? (
      <Stack spacing={2}>
        {comments.map((comment) => (
          <Card key={comment.id} variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Avatar
                  src={comment.author.avatar_url}
                  sx={{ mr: 2, width: 32, height: 32 }}
                  alt={`${comment.author.first_name} ${comment.author.last_name}`}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {comment.author.first_name} {comment.author.last_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatCreatedAt(comment.created_at)}
                  </Typography>
                </Box>
              </Box>

              {/* Horizontal Layout: Text on Left, Attachments on Right */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: { xs: 2, sm: 3 },
                  alignItems: { xs: "stretch", sm: "flex-start" },
                }}
              >
                {/* Left Side: Comment Text */}
                <Box
                  sx={{
                    flex: { xs: "none", sm: 1 },
                    minWidth: { sm: "300px" },
                  }}
                >
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {comment.comment || t("documentDetail.na")}
                  </Typography>

                  {/* Documents Section (stays with text) */}
                  {comment.attachments.length > 0 &&
                    (() => {
                      const documents = comment.attachments.filter(
                        (file) => !file.is_image
                      );
                      return (
                        documents.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 1.5,
                              }}
                            >
                              <AttachmentIcon
                                sx={{
                                  color: "secondary.main",
                                  fontSize: 16,
                                }}
                              />
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 600,
                                  fontSize: "0.75rem",
                                  color: "text.primary",
                                }}
                              >
                                {t(
                                  "documentDetail.documentsTitle",
                                  "Documents"
                                )}{" "}
                                ({documents.length})
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns:
                                  "repeat(auto-fill, minmax(80px, 1fr))",
                                gap: 1.5,
                              }}
                            >
                              {documents.map((attachment) => (
                                <Box
                                  key={attachment.id}
                                  onClick={() => onFileClick(attachment.url)}
                                  sx={{
                                    cursor: "pointer",
                                    borderRadius: 2,
                                    overflow: "hidden",
                                    border: "1px solid",
                                    borderColor: "divider",
                                    transition: "all 0.2s ease-in-out",
                                    "&:hover": {
                                      borderColor: "primary.main",
                                      transform: "translateY(-2px)",
                                      boxShadow: 2,
                                    },
                                  }}
                                >
                                  <Box sx={{ textAlign: "center", p: 1 }}>
                                    <AttachmentIcon
                                      sx={{
                                        fontSize: 20,
                                        color: "text.secondary",
                                        mb: 0.5,
                                      }}
                                    />
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        display: "block",
                                        fontWeight: 500,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        fontSize: "0.65rem",
                                      }}
                                    >
                                      {attachment.display_name ||
                                        t("documentDetail.fileLabel")}
                                    </Typography>
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        )
                      );
                    })()}
                </Box>

                {/* Right Side: Image Carousel */}
                {comment.attachments.length > 0 &&
                  (() => {
                    const images = comment.attachments.filter(
                      (file) => file.is_image
                    );
                    return (
                      images.length > 0 && (
                        <Box
                          sx={{
                            flex: { xs: "none", sm: "0 0 300px" },
                            width: { xs: "100%", sm: "300px" },
                            maxWidth: { xs: "100%", sm: "300px" },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 1.5,
                            }}
                          >
                            <ImageIcon
                              sx={{
                                color: "info.main",
                                fontSize: 16,
                              }}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 600,
                                fontSize: "0.75rem",
                                color: "text.primary",
                              }}
                            >
                              {t("documentDetail.imagesTitle", "Images")} (
                              {images.length})
                            </Typography>
                          </Box>

                          {/* Compact Image Carousel for Comments */}
                          <ImageCarousel
                            images={images}
                            onImageClick={onFileClick}
                            convertUTCToLocal={formatCreatedAt}
                            t={t}
                            compact={true}
                          />
                        </Box>
                      )
                    );
                  })()}
              </Box>
            </CardContent>
          </Card>
        ))}
      </Stack>
    ) : (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <CommentIcon sx={{ fontSize: 48, color: "text.disabled", mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          {t("documentDetail.noCommentsAvailable")}
        </Typography>
      </Box>
    )}
  </ModernCard>
));

CommentsTable.displayName = "CommentsTable";

CommentsTable.propTypes = {
  comments: PropTypes.array.isRequired,
  formatCreatedAt: PropTypes.func.isRequired,
  onFileClick: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

export default CommentsTable;
